import { Box } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as constants from "../../../../constants";
import CustomAutocomplete from "../../../../shared/AutoComplete";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../../shared/Button/customOutlinedButton";
import CustomDatePicker from "../../../../shared/CustomDate";
import CustomTextField from "../../../../shared/CustomTextFeild";
import ReusableTable from "../../../../shared/ReusableTable/ReusableTable";
import { RootState } from "../../../../store/store";
import { resetTableState, setPagination, setTableFilters } from "../../../../store/tableSlice";
import basestyle from "../../../CSS/Base.module.css";
import { getAllParticipants, getRcboInvoiceListDetails } from "../../services/RCBOInvoices/RCBOInvoiceServices";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer } from "../../StyledComponents/Container";
import { notifyUser } from "../../Utils/notifyUser";
import { getClientDetailsByClientName } from "../../Utils/ClientSettingsTable";

interface Column {
    id: string;
    label: string;
    minWidth: number | string;
    align?: "center" | "left" | "right";
    isHyperlink: boolean;
}


export const formatAmount = (
    amount: number | null | undefined,
    locale: string = "en-US",
    currency: string = "USD"
): string => {
    const formatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    return formatter.format(amount ?? 0);
};

function formatDate(dateString: string): string {
    const date = new Date(dateString);

    // Get the month, day, and year
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Add 1 since months are 0-based
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();

    // Return in MM/DD/YYYY format
    return `${month}/${day}/${year}`;
}

const STATUS_ALL = "All";
const STATUS_OPEN = "Open";
const STATUS_PAID = "Paid";
const STATUS_PAID_IN_FULL = "PaidInFull";
const statusOptions = [STATUS_ALL, STATUS_OPEN, STATUS_PAID];

const getApiStatus = (status: string) => status === STATUS_PAID ? STATUS_PAID_IN_FULL : status;
const getDisplayStatus = (status: string) => status === STATUS_PAID_IN_FULL ? STATUS_PAID : status;

const RcboSummaryListing = () => {
    const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);
    const userInfo = useSelector((state: RootState) => state.auth);
    const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
    let role: any = userInfo.roles?.roleName;
    let clientId = userInfo.clientId;
    const [userRole] = useState<string>(contactInformation.participantAdminProfile);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const dispatch = useDispatch();
    const filters = useSelector((state: RootState) => state.table.rcboInvoice?.filters || {});
    const pagination = useSelector((state: RootState) => state.table.rcboInvoice);

    const [count, setCount] = useState(0);
    const [page, setPage] = useState(pagination?.page || 1);
    const [rowsPerPage, setRowsPerPage] = useState(pagination?.pageSize || 5);
    const [filteredData, setFilteredData] = useState<any[]>([]);

    const [isLoading, setIsLoading] = useState(false);
    const [participantsList, setParticipantsList] = useState([]);
    const [initialFilters, setInitialFilters] = useState({
        invoiceNumber: filters?.invoiceNumber || "",
        participantId: filters?.participantId || 1,
        invoiceDateStart: filters?.invoiceDateStart || "",
        invoiceDateEnd: filters?.invoiceDateEnd || "",
        status: filters?.status || "Open",
    });

    const handlePageChange = (newPage: number) => {
        setPage(newPage + 1);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(1);
    };

    const handleIdClick = (rowData: any) => {
        navigate("/rcboInvoiceDetails", { state: rowData });
    };

    const handleSearch = () => {
        const { invoiceDateStart, invoiceDateEnd } = initialFilters;

        if (invoiceDateStart && invoiceDateEnd) {
            const startDate = new Date(invoiceDateStart);
            const endDate = new Date(invoiceDateEnd);

            if (endDate < startDate) {
                toast.error(translate("toDateShouldBeGreaterThanFromDate"));
                return;
            }
        }

        setPage(1);
        getInvoiceSummaryListInfo(initialFilters, 1, rowsPerPage);
    };
    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };
    const handleClear = () => {
        dispatch(resetTableState({ tableKey: 'rcboInvoice' }));
        const updatedFilters = {
            ...initialFilters,
            invoiceNumber: "",
            participantId: 1,
            invoiceDateStart: "",
            invoiceDateEnd: "",
            status: "Open",
        }
        setInitialFilters(updatedFilters)
        setPage(1);
        setRowsPerPage(5);
        getInvoiceSummaryListInfo(updatedFilters, 1, 5);
    }

    const restrictedRoles = [
        ...(ClientDetails?.name !== constants.CLIENT_NAME_DC_GARY ? [constants.ROLES_FINANCIAL_ANALYST] : []),
        constants.ROLES_READ_ONLY_USER,
        constants.ROLES_PROJECT_APPROVER,
        constants.ROLES_BUDGET_ANALYST,
    ];

    const columnNames: any = [
        { id: "invoiceNumber", label: translate('invoiceNumber'), minWidth: "120px", align: "center", isHyperlink: true },
        { id: "postingDescription", label: translate('description'), minWidth: "150px", isHyperlink: false },
        { id: "amount", label: translate('amount'), minWidth: "100px", align: "right", isHyperlink: false },
        { id: "outstandingBalance", label: translate('outstandingBalance'), minWidth: "150px", align: "right", isHyperlink: false },
        { id: "invoiceDate", label: translate('invoiceDate'), minWidth: "100px", align: "center", isHyperlink: false },
        { id: "dueDate", label: translate('dueDate'), minWidth: "100px", align: "center", isHyperlink: false },
        ...(userRole === constants.ADMIN_VIEW ? [{ id: "participantName", label: translate("participantName"), minWidth: "150px", isHyperlink: false }] : []),
        ...(!restrictedRoles.includes(role) ?
            [{ id: "RCBOInvoicepayment", label: translate('payment'), minWidth: "150px", align: "center", isHyperlink: true }]
            : []),
        { id: "status", label: translate('status'), minWidth: "100px", align: "center", isHyperlink: false },
    ];

    async function fetchParticipants() {
        let participantVO = {
            page: -1,
            size: -1,
            isActive: constants.IS_ACTIVE_YES,
            clientId: userInfo.clientId
        };
        try {
            const response = await getAllParticipants(participantVO);
            if (response.data?.status === constants.STATUS_CODES.SUCCESS) {
                const participants = response.data?.data?.data?.map((item: any) => ({
                    participantId: item.participantId,
                    participantName: item.participantName,
                })) || [];

                // Prepend "All" option
                participants.unshift({
                    participantId: 1,
                    participantName: "All",
                });
                setParticipantsList(participants);
            } else {
                notifyUser(response.error?.data.message, 'error', translate('rCBOInvoiceSummary'));
            }
        } catch (error) {
            console.warn(error);
        }
    }

    async function getInvoiceSummaryListInfo(filterObj: any, page: number, rowsPerPage: number) {
        setIsLoading(true);
        try {
            const selectedStatus = getDisplayStatus(filterObj.status);
            const baseVo = {
                participantId: userRole === constants.ADMIN_VIEW ? filterObj.participantId : userInfo?.defaultParticipantId,
                invoiceNumber: filterObj.invoiceNumber,
                invoiceDateStart: filterObj.invoiceDateStart,
                invoiceDateEnd: filterObj.invoiceDateEnd,
            };
            const tableFilters = {
                ...baseVo,
                status: selectedStatus,
            };
            const requestedStatuses = selectedStatus === STATUS_ALL ? [STATUS_OPEN, STATUS_PAID_IN_FULL] : [getApiStatus(selectedStatus)];

            dispatch(setTableFilters({ tableKey: 'rcboInvoice', filters: tableFilters }));
            dispatch(setPagination({ tableKey: 'rcboInvoice', page: page, pageSize: rowsPerPage }));
            const responses = await Promise.all(
                requestedStatuses.map((status) => getRcboInvoiceListDetails({ ...baseVo, status }))
            );
            const failedResponse = responses.find((response) => response.status !== constants.STATUS_CODES.SUCCESS);

            if (!failedResponse) {
                const allData = responses.flatMap((response) => response.data.data || []);
                if (allData != null) {
                    setCount(allData.length);

                    // ✅ Sort data by invoiceNumber (descending)
                    const sortedData = [...allData].sort((a: any, b: any) => {
                        const numA = parseInt(a.invoiceNumber.replace(/\D/g, ""), 10);
                        const numB = parseInt(b.invoiceNumber.replace(/\D/g, ""), 10);
                        return numB - numA; // descending order
                    });

                    // Calculate the slice index based on the current page and rowsPerPage
                    const pages = page - 1;
                    const startIndex = pages * rowsPerPage;
                    const endIndex = startIndex + rowsPerPage;

                    // Slice the data based on pagination and format the necessary fields
                    const paginatedData = sortedData.slice(startIndex, endIndex).map((item: any) => ({
                        ...item,
                        invoiceDate: formatDate(item.invoiceDate),
                        dueDate: formatDate(item.dueDate),
                        amount: formatAmount(item.amount),
                        outstandingBalance: formatAmount(item.outstandingBalance),
                        RCBOInvoicepayment: userRole === constants.ADMIN_VIEW
                            ? "Payment Pending"
                            : (item.status === true ? "Make Payment" : "-"),
                        status: item.status === true ? "Open" : "Paid"
                    }));

                    // Set the filtered data for the current page
                    setFilteredData(paginatedData);
                } else {
                    setCount(0);
                    setFilteredData([]);
                }
            }
            else {
                setCount(0);
                setFilteredData([]);
                notifyUser(failedResponse.response?.data?.message, 'error', translate('rCBOInvoiceSummary'));
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getInvoiceSummaryListInfo(initialFilters, page, rowsPerPage);
    }, [initialFilters.participantId, initialFilters.status, page, rowsPerPage])

    useEffect(() => {
        if (userRole === constants.ADMIN_VIEW) {
            fetchParticipants();
        }
    }, [userInfo])

    useEffect(() => {
        if (location.state?.message) {
            notifyUser(location.state.message, 'success', translate('rCBOInvoiceSummary'));
            navigate(location.pathname, { replace: true });
        }
    }, [location, navigate]);
    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{clientId != 2 ? translate('rCBOInvoiceSummary') : translate('internalInvoiceSummary')}</SectionTitle>
                    <Box sx={{ width: "100%", px: 1.5 }}>

                        <div className="search-fields-container">
                            <div className="left-fields">
                                <CustomTextField
                                    sx={{ width: 250 }}
                                    label={translate('invoiceNumber')}
                                    value={initialFilters.invoiceNumber}
                                    onKeyDown={handleKeyPress}
                                    onChange={(e) => setInitialFilters((prevFilters) => ({
                                        ...prevFilters,
                                        invoiceNumber: e.target.value.trim(),
                                    }))}
                                    inputProps={{
                                        maxLength: 15
                                    }}
                                />
                                {userRole === constants.ADMIN_VIEW && <CustomAutocomplete
                                    sx={{ width: 250 }}
                                    options={participantsList}
                                    getOptionLabel={(option) => option.participantName || ""}
                                    isOptionEqualToValue={(option, value) => option.participantName === value.participantName}
                                    value={
                                        participantsList.find(
                                            (p: any) => p.participantId === initialFilters.participantId
                                        ) || {
                                            participantId: 1,
                                            participantName: "All",
                                        }
                                    }
                                    onChange={(event, newValue) => {
                                        if (!newValue) return;
                                        setInitialFilters((prevFilters) => ({
                                            ...prevFilters,
                                            participantId: newValue.participantId,
                                        }));

                                    }}
                                    onBlur={() => {
                                        if (!participantsList) {
                                            setInitialFilters((prevFilters) => ({
                                                ...prevFilters,
                                                participantId: 1,
                                            }));
                                        }
                                    }}
                                    label={translate('selectParticipant')}
                                />}
                                <CustomDatePicker
                                    sx={{ width: { xs: "100%", sm: 180 } }}
                                    label={translate('fromDate')}
                                    value={initialFilters.invoiceDateStart ? dayjs(initialFilters.invoiceDateStart) : null}
                                    onChange={(newValue) => setInitialFilters((prevFilters) => ({
                                        ...prevFilters,
                                        invoiceDateStart: newValue ? newValue.format("MM/DD/YYYY") : "",
                                    }))}
                                />
                                <CustomDatePicker
                                    sx={{ width: { xs: "100%", sm: 180 } }}
                                    label={translate('toDate')}
                                    value={initialFilters.invoiceDateEnd ? dayjs(initialFilters.invoiceDateEnd) : null}
                                    onChange={(newValue) => setInitialFilters((prevFilters) => ({
                                        ...prevFilters,
                                        invoiceDateEnd: newValue ? newValue.format("MM/DD/YYYY") : "",
                                    }))} />
                                <CustomAutocomplete
                                    sx={{ width: { xs: "100%", sm: 180 } }}
                                    options={statusOptions}
                                    value={getDisplayStatus(initialFilters.status)}
                                    onChange={(event, newValue) => {
                                        if (newValue !== null) {
                                            setInitialFilters((prevFilters) => ({
                                                ...prevFilters,
                                                status: newValue,
                                            }));
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!initialFilters.status) {
                                            setInitialFilters((prevFilters) => ({
                                                ...prevFilters,
                                                status: STATUS_OPEN,
                                            }));
                                        }
                                    }}
                                    placeholder={translate('status')}
                                    label={translate('status')}
                                />

                                <ContainedButton onClick={handleSearch}> {translate('search')} </ContainedButton>
                                <OutlinedButton onClick={handleClear}> {translate('clear')} </OutlinedButton>
                            </div>
                        </div>
                        <ReusableTable
                            columns={columnNames}
                            rows={filteredData}
                            count={count}
                            page={page - 1}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            onRowClick={handleIdClick}
                            loading={isLoading}
                        />
                        <div className={basestyle.buttoncontainer}>
                            <ContainedButton onClick={() => userRole === constants.ADMIN_VIEW ? navigate("/rcboSummary") : navigate("/summary")}>
                                {translate('back')}</ContainedButton>
                        </div>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    )
}

export default RcboSummaryListing;